/**
 * Pointer-based long-press hook for touch and mouse hold gestures.
 * Short taps invoke onClick; holds longer than the threshold invoke onLongPress.
 */

import { useCallback, useEffect, useRef } from "react";
import {
    DEFAULT_LONG_PRESS_MS,
    LONG_PRESS_MOVE_TOLERANCE_PX,
} from "../components/FloatingActions/FloatingActionBurst/floatingActionBurst.configuration";

interface UseLongPressOptions {

    /** Called when the pointer is released before the long-press threshold. */
    "onClick"?: () => void;

    /** Called when the pointer is held past the long-press threshold. */
    "onLongPress"?: () => void;

    /** When false, only onClick is wired (no long-press timer). */
    "enabled"?: boolean;

    /** Hold duration in milliseconds before onLongPress fires. */
    "delayMs"?: number;
}

interface LongPressBindProps {
    "onPointerDown":   ( event: React.PointerEvent<HTMLElement> ) => void;
    "onPointerUp":     ( event: React.PointerEvent<HTMLElement> ) => void;
    "onPointerMove":   ( event: React.PointerEvent<HTMLElement> ) => void;
    "onPointerCancel": ( event: React.PointerEvent<HTMLElement> ) => void;
    "onClick":         ( event: React.MouseEvent<HTMLElement> ) => void;
}

interface UseLongPressResult { "bind": LongPressBindProps }

/**
 * Returns pointer handlers that distinguish short taps from long presses.
 *
 * @param options - Click and long-press callbacks plus optional timing overrides.
 * @returns Event bindings to spread onto a pressable element.
 */
export const useLongPress = ( {
    onClick,
    onLongPress,
    enabled = true,
    delayMs = DEFAULT_LONG_PRESS_MS,
}: UseLongPressOptions ): UseLongPressResult => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>( null );
    const startPositionRef = useRef<{
        "x": number;
        "y": number;
    } | null>( null );
    const longPressTriggeredRef = useRef( false );
    const onClickRef = useRef( onClick );
    const onLongPressRef = useRef( onLongPress );

    useEffect( () => {
        onClickRef.current = onClick;
    }, [ onClick ] );

    useEffect( () => {
        onLongPressRef.current = onLongPress;
    }, [ onLongPress ] );

    const clearTimer = useCallback( (): void => {
        if ( timerRef.current !== null ) {
            clearTimeout( timerRef.current );
            timerRef.current = null;
        }
    }, [] );

    const handlePointerDown = useCallback( ( event: React.PointerEvent<HTMLElement> ): void => {
        if ( !enabled ) {
            return;
        }

        clearTimer();
        longPressTriggeredRef.current = false;
        startPositionRef.current = {
            "x": event.clientX,
            "y": event.clientY,
        };

        if ( onLongPressRef.current === undefined ) {
            return;
        }

        timerRef.current = setTimeout( () => {
            longPressTriggeredRef.current = true;
            onLongPressRef.current?.();
        }, delayMs );
    }, [ clearTimer, delayMs, enabled ] );

    const handlePointerMove = useCallback( ( event: React.PointerEvent<HTMLElement> ): void => {
        const startPosition = startPositionRef.current;
        if ( startPosition === null ) {
            return;
        }

        const deltaX = Math.abs( event.clientX - startPosition.x );
        const deltaY = Math.abs( event.clientY - startPosition.y );
        if ( deltaX > LONG_PRESS_MOVE_TOLERANCE_PX || deltaY > LONG_PRESS_MOVE_TOLERANCE_PX ) {
            clearTimer();
            startPositionRef.current = null;
        }
    }, [ clearTimer ] );

    const resetGesture = useCallback( (): void => {
        clearTimer();
        startPositionRef.current = null;
    }, [ clearTimer ] );

    const handlePointerUp = useCallback( ( event: React.PointerEvent<HTMLElement> ): void => {
        if ( !enabled ) {
            return;
        }

        const wasLongPress = longPressTriggeredRef.current;
        resetGesture();

        if ( !wasLongPress && onClickRef.current !== undefined ) {
            onClickRef.current();
        }

        if ( wasLongPress ) {
            event.preventDefault();
        }
    }, [ enabled, resetGesture ] );

    const handlePointerCancel = useCallback( (): void => {
        resetGesture();
        longPressTriggeredRef.current = false;
    }, [ resetGesture ] );

    const handleClick = useCallback( ( event: React.MouseEvent<HTMLElement> ): void => {
        if ( longPressTriggeredRef.current ) {
            event.preventDefault();
            event.stopPropagation();
            longPressTriggeredRef.current = false;
        }
    }, [] );

    useEffect( () => {
        return () => {
            clearTimer();
        };
    }, [ clearTimer ] );

    return {
        "bind": {
            "onPointerDown":   handlePointerDown,
            "onPointerUp":     handlePointerUp,
            "onPointerMove":   handlePointerMove,
            "onPointerCancel": handlePointerCancel,
            "onClick":         handleClick,
        },
    };
};
